<template>
  <v-data-table
    ref="DataTable"
    v-model="model"
    :class="classes"
    :dense="dense"
    :custom-group="customGroup"
    :custom-sort="customSort"
    :disable-pagination="disablePagination"
    :disable-sort="disableSort"
    :fixed-header="fixedHeader"
    :group-by="groupBy"
    :group-desc="groupDesc"
    :headers="tableHeaders"
    :hide-default-footer="hideDefaultFooter"
    :hide-default-header="hideDefaultHeader"
    :item-key="itemKey"
    :items="showFilters ? filterItems : items"
    :items-per-page="itemsPerPage"
    :loading="loading"
    :locale="locale ? locale : $store.getters.locale"
    :page="page"
    :search="search"
    :show-expand="showExpand"
    :show-group-by="showGroupBy"
    :show-select="showSelect"
    :single-expand="singleExpand"
    :single-select="singleSelect"
    :sort-by="sortBy"
    :sort-desc="sortDesc"
    @click:row="clickRow"
    @item-expanded="itemExpanded"
    @item-selected="itemSelected"
    @page-count="pageCount"
    @pagination="pagination"
    @update:group-by="updateGroupBy"
    @update:group-desc="updateGroupDesc"
    @update:options="updateOptions"
    @update:sort-by="updateSortBy"
    @update:sort-desc="updateSortDesc"
  >
    <!-- Custom headers -->
    <template v-for="(header, index) in tableHeaders" v-slot:[`header.${header.value}`]>
      <DataTableResizeCol
        v-if="allowColumnResizing && index + 1 < tableHeaders.length"
        :key="header.value"
        @updateWidth="header.width = $event"
      />
      <span class="header-name" :key="`${header.value}_hn`" :title="header.text">{{ header.text }}</span>
    </template>
    <!-- Data Table filters -->
    <template v-if="showFilters && !loading" #body.prepend>
      <DataTableFilters
        :headers="tableHeaders"
        :items="items"
        :show-select="showSelect"
        @filterResult="filterResult"
      />
    </template>
    <!-- Group header new design -->
    <template #group.header="{ group, items, headers, isOpen, remove, toggle }">
      <td :colspan="headers.length">
        <v-btn
          icon
          small
          class="group-toggle-button mr-1"
          @click.prevent="toggle"
        >
          <v-icon v-text="isOpen ? 'mdi-menu-down' : 'mdi-menu-right'" />
        </v-btn>
        <span>{{ groupByText }}: {{ group }} ({{ items.length }})</span>
        <v-btn
          icon
          small
          class="group-remove-button float-right"
          :title="groupByText"
          @click.prevent="remove"
        >
          <v-icon v-text="'mdi-close'" />
        </v-btn>
      </td>
    </template>
    <!-- Pass slots to VDataTable -->
    <template v-for="(index, name) in $slots" v-slot:[name]>
      <slot :name="name"></slot>
    </template>
    <template v-for="(index, name) in $scopedSlots" v-slot:[name]="data">
      <slot :name="name" v-bind="data"></slot>
    </template>
    <!-- Disable default top slot -->
    <template #top>
      <div
        class="small-buttons-bar d-flex"
        v-if="columnSelector || hasReloadEvent || (!!$slots.buttons || !!$scopedSlots.buttons)"
      >
        <div class="small-buttons-bar-col flex-fill">
          <slot name="buttons"></slot>
        </div>
        <div v-if="columnSelector || hasReloadEvent" class="small-buttons-bar-col text-no-wrap">
          <v-menu
            v-if="columnSelector"
            left
            offset-y
            :close-on-content-click="false"
            :disabled="loading || !menuHeaders.length"
            max-height="calc(100vh - 24px)"
          >
            <template #activator="{ on, attrs }">
              <v-btn
                small
                icon
                :disabled="loading || !menuHeaders.length"
                :title="columnSelectorButton"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon size="20">mdi-table-cog</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item-group
                v-model="selectedHeaders"
                multiple
                color="primary"
              >
                <v-list-item
                  v-for="(header, index) in menuHeaders"
                  :key="index"
                  :value="header.value"
                >
                  <template #default="{ active }">
                    <v-list-item-icon class="mr-2">
                      <v-icon size="22" v-text="active ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline'" />
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>{{ header.text }}</v-list-item-title>
                    </v-list-item-content>
                  </template>
                </v-list-item>
              </v-list-item-group>
            </v-list>
          </v-menu>
          <v-btn
            v-if="hasReloadEvent"
            small
            icon
            color="primary"
            class="ml-1"
            :disabled="loading || !menuHeaders.length"
            :title="reloadButton"
            @click.prevent="clickReload"
          >
            <v-icon size="20">mdi-reload</v-icon>
          </v-btn>
        </div>
      </div>
      <slot name="top"></slot>
    </template>
  </v-data-table>
</template>

<script lang="ts">
/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-var-requires, @typescript-eslint/ban-ts-comment */
import Vue from 'vue';
import { PropValidator } from 'vue/types/options';
import { DataGroupFunction, DataPagination, DataSortFunction } from 'vuetify/types';
// @ts-ignore
import { groupItems, sortItems } from 'vuetify/lib/util/helpers';
import { Header, ItemProps } from '@/types/DataTable';
import { each } from '@/utils/helpers';
import intl from '@/utils/intl';

import DataTableFilters from '@/components/data-table/data-table-filters.vue';
import DataTableResizeCol from '@/components/data-table/data-table-resize-col.vue';

export default Vue.extend({
  name: 'VCustomDataTable',
  components: {
    DataTableFilters,
    DataTableResizeCol
  },
  props: {
    allowColumnResizing: {
      type: Boolean,
      default: false
    },
    columnSelector: {
      type: Boolean,
      default: true
    },
    customGroup: {
      type: Function,
      default: groupItems
    } as PropValidator<DataGroupFunction>,
    customSort: {
      type: Function,
      default: sortItems
    } as PropValidator<DataSortFunction>,
    dense: Boolean,
    disablePagination: {
      type: Boolean,
      default: false
    },
    disableSort: {
      type: Boolean,
      default: false
    },
    groupBy: {
      type: [String, Array],
      default: () => []
    } as PropValidator<string | string[]>,
    groupDesc: {
      type: [Boolean, Array],
      default: () => []
    } as PropValidator<boolean | boolean[]>,
    fixedHeader: {
      type: Boolean,
      default: false
    },
    headers: {
      type: Array,
      default: () => []
    } as PropValidator<Header[]>,
    hideDefaultFooter: {
      type: Boolean,
      default: false
    },
    hideDefaultHeader: {
      type: Boolean,
      default: false
    },
    itemKey: String,
    items: {
      type: Array,
      default: () => []
    } as PropValidator<any[]>,
    itemsPerPage: {
      type: Number,
      default: 10
    },
    loading: {
      type: Boolean,
      default: false
    },
    locale: {
      type: String,
      default: ''
    },
    page: {
      type: Number,
      default: 1
    },
    search: String,
    showExpand: {
      type: Boolean,
      default: false
    },
    showGroupBy: {
      type: Boolean,
      default: false
    },
    showSelect: {
      type: Boolean,
      default: false
    },
    showFilters: {
      type: Boolean,
      default: false
    },
    singleExpand: {
      type: Boolean,
      default: false
    },
    singleSelect: {
      type: Boolean,
      default: false
    },
    sortBy: {
      type: [String, Array]
    } as PropValidator<string | string[] | undefined>,
    sortDesc: {
      type: [Boolean, Array]
    } as PropValidator<boolean | boolean[] | undefined>,
    value: {
      type: Array,
      default: () => []
    } as PropValidator<any[]>,
    wrapperStyles: String
  },
  data() {
    return {
      filterItems: [] as Array<any>,
      selectedHeaders: [] as Array<string>,
      tableHeaders: [] as Array<Header>,
      internalGroupBy: Array.isArray(this.groupBy) ? this.groupBy : [this.groupBy] as Array<string>
    }
  },
  computed: {
    model: {
      get(): any {
        return this.value;
      },
      set(value: any): void {
        this.$emit('input', value);
      }
    },
    classes(): Record<string, boolean> {
      return {
        'v-custom-data-table': true,
        'resizable': this.allowColumnResizing
      }
    },
    menuHeaders(): Array<Header> {
      return this.headers
        .filter(item => item.text && item.value !== 'data-table-expand')
        .filter(item => !this.internalGroupBy.includes(item.value));
    },
    hasReloadEvent(): boolean {
      return 'click:reload' in this.$listeners;
    },
    groupByText(): string {
      return this.tableHeaders.find((header: Header) => header.value === this.internalGroupBy[0])?.text ?? '';
    },
    columnSelectorButton(): string {
      return intl.t('dataTable.columnSelector');
    },
    reloadButton(): string {
      return intl.t('dataTable.reloadText');
    }
  },
  methods: {
    clickReload(): void {
      this.$emit('click:reload');
    },
    clickRow(item: any, data: ItemProps): void {
      this.$emit('click:row', item, data);
    },
    itemExpanded(item: any, value: boolean): void {
      this.$emit('item-expanded', item, value);
    },
    itemSelected(item: any, value: boolean): void {
      this.$emit('item-selected', item, value);
    },
    pageCount(value: number): void {
      this.$emit('page-count', value);
    },
    pagination(value: DataPagination): void {
      this.$emit('pagination', value);
    },
    updateGroupBy(item: string | string[]): void {
      this.$emit('update:group-by', item);
    },
    updateGroupDesc(item: string | string[]): void {
      this.$emit('update:group-desc', item);
    },
    updateOptions(item: any): void {
      this.internalGroupBy = item.groupBy || [];
      if (this.showGroupBy) {
        this.updateGroupByHeaders();
      }
      this.$emit('update:options', item);
    },
    updateSortBy(item: string | string[]): void {
      this.$emit('update:sort-by', item);
    },
    updateSortDesc(item: string | string[]): void {
      this.$emit('update:sort-desc', item);
    },
    filterResult(items: Array<any>): void {
      this.filterItems = items;
    },
    // Custom
    updateGroupByHeaders(): void {
      this.$nextTick(() => {
        each(this.$el.querySelectorAll('table thead.v-data-table-header th'), (th: HTMLTableElement, idx: number) => {
          const span = [...th.querySelectorAll('span')]
            .filter((span: HTMLSpanElement) => span.innerText === 'group')
            .shift();
          if (span) {
            if (this.singleSelect && idx === 0) {
              span.remove();
            } else {
              span.title = 'group';
              span.setAttribute('class', 'header-group v-icon notranslate mdi mdi-plus-box');
              span.setAttribute('style', 'font-size:18px; height:18px; width:18px');
              span.setAttribute('aria-hidden', 'true');
              span.innerHTML = '';
            }
          }
        });
      });
    }
  },
  watch: {
    headers: {
      handler(data: Array<Header>): void {
        this.tableHeaders = [];
        this.selectedHeaders = [];
        each(data, (header: Header) => {
          if (header.hidden !== true) {
            this.tableHeaders.push(header);
            this.selectedHeaders.push(header.value);
          }
        });
      },
      immediate: true
    },
    showGroupBy: {
      handler(value: boolean): void {
        if (value) {
          this.updateGroupByHeaders();
        }
      },
      immediate: true
    },
    wrapperStyles: {
      handler(value: string): void {
        // Set Data Table Wrapper styles
        this.$nextTick(() => {
          const wrapper = Array.from(this.$el.children).find(item => item.className.includes('v-data-table__wrapper')) as HTMLElement;
          if (value && wrapper) {
            wrapper.setAttribute('style', value.trim());
          }
        });
      },
      immediate: true
    },
    selectedHeaders(data: Array<string>): void {
      let headers: Array<Header> = [];
      each(data, (val: string) => {
        const header = this.headers.find(item => item.value == val);
        if (header) {
          headers.push(header);
        }
      });
      headers = headers.sort((a: Header, b: Header) => {
        const indexA = this.headers.findIndex(item => item.value == a.value);
        const indexB = this.headers.findIndex(item => item.value == b.value);

        return indexA - indexB;
      });
      this.tableHeaders = [];
      this.tableHeaders = headers;
      this.$emit('update:selectedHeaders', data);
      if (this.showGroupBy) {
        this.updateGroupByHeaders();
      }
    },
    '$vuetify.breakpoint.smAndUp'(value: boolean): void {
      if (this.showGroupBy && value) {
        this.updateGroupByHeaders();
      }
    }
  }
});
</script>

<style lang="scss">
$data-table-cell-padding: 14px;

.v-custom-data-table {
  > .v-data-table__wrapper {
    > table {
      thead tr th {
        padding: 0 $data-table-cell-padding;
        .header-name {
          display: inline-block;
        }
        &.sortable {
          &.text-start, &.text-center {
            .v-data-table-header__icon {
              float: right;
            }
          }
          &.text-end {
            .v-data-table-header__icon {
              float: left;
            }
          }
          .header-name {
            float: left;
            min-width: 20px;
            max-width: calc(100% - 18px);
            width: calc(100% - 18px);
          }
        }
        &:has(.header-group) {
          .header-group {
            float: right;
            cursor: pointer;
            &:hover {
              color: rgba(0, 0, 0, 0.87);
            }
          }
          .header-name {
            max-width: calc(100% - 18px);
            width: calc(100% - 18px);
          }
        }
        &.sortable:has(.header-group) {
          .header-name {
            max-width: calc(100% - 18px - 18px);
            width: calc(100% - 18px - 18px);
          }
        }
      }
      tbody tr td {
        padding: 0 $data-table-cell-padding;
      }
      /*$selected-color: #1976d2;
      tbody tr.v-data-table__selected {
        background: rgba($selected-color, .12) !important;
        td, .v-data-table__checkbox .v-icon {
          color: $selected-color !important;
          caret-color: $selected-color;
        }
      }*/
    }
  }
  .small-buttons-bar {
    margin: 0;
    .small-buttons-bar-col {
      line-height: 33px;
      padding: 6px 12px;
    }
  }
  &.resizable:not(.v-data-table--mobile) {
    > .v-data-table__wrapper {
      > table {
        thead {
          tr th:not(.header-resize-wrap) {
            .header-name {
              white-space: nowrap;
              text-overflow: ellipsis;
              overflow: hidden;
            }
          }
        }
        tbody tr:not(.v-data-table-filter-row) td {
          min-width: ($data-table-cell-padding * 2) + 24px;
          max-width: 0;
          white-space: nowrap;
          text-overflow: ellipsis;
          overflow: hidden;
        }
      }
    }
  }
  &.resizable:not(.v-data-table--fixed-header) {
    > .v-data-table__wrapper {
      > table {
        thead {
          tr th {
            position: relative;
          }
        }
      }
    }
  }
}
</style>
